import { Component, OnDestroy, TemplateRef } from '@angular/core';

import { ORBI_TOAST_SCOPE } from '@patient-ui/shared/constants';
import { ToastService } from '@patient-ui/shared-ui/utils';

/** The toast fields the Orbi template reads (ToastService stores them untyped). */
export interface OrbiToast {
  textOrTpl: string | TemplateRef<unknown>;
  classname?: string;
  icon?: string;
  delay?: number;
  scope?: string;
}

/**
 * Toast renderer for the Orbi panel (PCSA-13169 / PCSA-13171). Rendering is the
 * shared `patient-ui-app-toasts` container, scoped to `ORBI_TOAST_SCOPE` so Orbi
 * toasts show only here and app toasts never do. This component supplies the
 * Orbi look (the template + SCSS: green band, white-circle check, grey close)
 * and docks it top-right in the panel.
 */
@Component({
  selector: 'patient-ui-orbi-toasts',
  templateUrl: './orbi-toasts.component.html',
  styleUrls: ['./orbi-toasts.component.scss'],
})
export class OrbiToastsComponent implements OnDestroy {
  readonly scope = ORBI_TOAST_SCOPE;

  constructor(private readonly toastService: ToastService) {}

  isTemplate(toast: OrbiToast): boolean {
    return toast.textOrTpl instanceof TemplateRef;
  }

  remove(toast: OrbiToast): void {
    this.toastService.remove(toast);
  }

  /**
   * Drain the ORBI toasts still queued when this renderer is torn down
   * (PCSA-13312). The renderer lives inside the panel's `*ngIf="open"`, so
   * closing the panel destroys it before `ngb-toast`'s autohide timer can fire —
   * leaving the toast in the shared root queue, which then re-appears the next
   * time the panel is opened (the stale-toast bug). Only Orbi-scoped toasts are
   * removed so unrelated app-level toasts in the shared queue are left intact.
   */
  ngOnDestroy(): void {
    (this.toastService.toasts as OrbiToast[])
      .filter((toast) => toast.scope === ORBI_TOAST_SCOPE)
      .forEach((toast) => this.toastService.remove(toast));
  }
}
